import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import { getAssignedSurvey, submitAssignedSurvey } from '../../services/userService';

function SurveyFill() {
  const navigate = useNavigate();
  const { id } = useParams();
  const surveyId = Number(id);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const [answers, setAnswers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const totalQuestions = questions.length;
  const answeredCount = useMemo(() => {
    let count = 0;
    for (const q of questions) {
      const a = answers[q.id];
      if (!a) continue;
      if (q.type === 'text') {
        if (String(a.answer_text || '').trim()) count += 1;
      } else if (q.type === 'single_choice') {
        if (Number(a.selected_option_id)) count += 1;
      } else {
        if (Array.isArray(a.selected_option_ids) && a.selected_option_ids.length > 0) count += 1;
      }
    }
    return count;
  }, [answers, questions]);

  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const fetchSurvey = async () => {
    if (!surveyId) {
      setError('Invalid survey');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const resp = await getAssignedSurvey(surveyId);
      if (!resp?.success) {
        setError(resp?.message || 'Survey not available');
        setSurvey(null);
        setQuestions([]);
        return;
      }
      setSurvey(resp.data.survey);
      setQuestions(resp.data.questions || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Survey not available');
      setSurvey(null);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  const setAnswer = (questionId, payload) => {
    setAnswers((prev) => ({ ...prev, [questionId]: payload }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    for (const q of questions) {
      if (!q.is_required) continue;
      const a = answers[q.id];
      if (!a) {
        nextErrors[q.id] = 'Required';
        continue;
      }
      if (q.type === 'text') {
        if (!String(a.answer_text || '').trim()) nextErrors[q.id] = 'Required';
      } else if (q.type === 'single_choice') {
        if (!Number(a.selected_option_id)) nextErrors[q.id] = 'Required';
      } else {
        if (!Array.isArray(a.selected_option_ids) || a.selected_option_ids.length === 0) nextErrors[q.id] = 'Required';
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildAnswersArray = () => {
    return questions
      .map((q) => {
        const a = answers[q.id];
        if (!a) return null;
        if (q.type === 'text') return { question_id: q.id, answer_text: a.answer_text || '' };
        if (q.type === 'single_choice') return { question_id: q.id, selected_option_id: a.selected_option_id };
        return { question_id: q.id, selected_option_ids: a.selected_option_ids || [] };
      })
      .filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fill required questions');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await submitAssignedSurvey(surveyId, {
        answers: buildAnswersArray(),
      });

      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to submit survey');
        return;
      }

      setDone(true);
      toast.success('Survey submitted');
    } catch (e2) {
      const msg = e2?.response?.data?.message || 'Failed to submit survey';
      toast.error(msg);
      if (e2?.response?.data?.errors) {
        const apiErrors = {};
        for (const er of e2.response.data.errors) {
          apiErrors[er.question_id] = er.message || 'Required';
        }
        setFieldErrors(apiErrors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader message="Loading survey..." />;

  if (error) {
    return (
      <div>
        <button
          type="button"
          onClick={() => navigate('/user/survey')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-3"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back
        </button>
        <NoData message={error} />
      </div>
    );
  }

  if (!survey) {
    return <NoData message="Survey not available" />;
  }

  if (done) {
    return (
      <div className="min-h-[300px] flex items-center justify-center py-10">
        <div className="w-full max-w-xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <Icon icon="fluent:checkmark-circle-24-filled" width="28" height="28" className="text-emerald-600" />
              </div>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Submitted</h1>
            <p className="text-gray-500 text-sm mt-2">Thanks! Your response has been recorded.</p>
            <button
              type="button"
              onClick={() => navigate('/user/survey')}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:opacity-95"
            >
              <Icon icon="fluent:list-24-regular" width="18" height="18" />
              Back to Surveys
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate('/user/survey')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-3"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 break-words">{survey.title}</h1>
          {survey.description ? <p className="text-gray-500 text-sm mt-2 whitespace-pre-wrap">{survey.description}</p> : null}
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Progress</div>
          <div className="text-sm font-semibold text-gray-900">{progress}%</div>
        </div>
      </div>

      <div className="mb-5">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-2 bg-primary" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {questions.map((q, idx) => {
          const err = fieldErrors[q.id];
          return (
            <div key={q.id} className={`rounded-2xl border ${err ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white'} p-4`}>
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm font-semibold text-gray-900 break-words">
                  {idx + 1}. {q.question_text}
                  {q.is_required ? <span className="text-red-500"> *</span> : null}
                </div>
                {err ? <div className="text-xs text-red-600 font-medium">{err}</div> : null}
              </div>

              {q.type === 'text' ? (
                <textarea
                  className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  rows={3}
                  value={answers[q.id]?.answer_text || ''}
                  onChange={(e) => setAnswer(q.id, { answer_text: e.target.value })}
                  placeholder="Type your answer..."
                />
              ) : null}

              {q.type === 'single_choice' ? (
                <div className="mt-3 space-y-2">
                  {(q.options || []).map((o) => {
                    const checked = Number(answers[q.id]?.selected_option_id) === Number(o.id);
                    return (
                      <label
                        key={o.id}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition ${
                          checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          checked={checked}
                          onChange={() => setAnswer(q.id, { selected_option_id: o.id })}
                        />
                        <span className="text-sm text-gray-800">{o.option_text}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}

              {q.type === 'multi_choice' ? (
                <div className="mt-3 space-y-2">
                  {(q.options || []).map((o) => {
                    const arr = Array.isArray(answers[q.id]?.selected_option_ids)
                      ? answers[q.id].selected_option_ids
                      : [];
                    const checked = arr.includes(o.id);
                    return (
                      <label
                        key={o.id}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition ${
                          checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? arr.filter((x) => x !== o.id) : [...arr, o.id];
                            setAnswer(q.id, { selected_option_ids: next });
                          }}
                        />
                        <span className="text-sm text-gray-800">{o.option_text}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="text-xs text-gray-500">
            {answeredCount} of {totalQuestions} answered
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:opacity-95 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting
              </>
            ) : (
              <>
                <Icon icon="fluent:send-24-regular" width="18" height="18" />
                Submit
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SurveyFill;
